import React, {useEffect, useRef} from 'react';
import {Linking} from 'react-native';
import {
  DefaultTheme,
  getStateFromPath,
  InitialState,
  LinkingOptions,
  NavigationContainer,
} from '@react-navigation/native';

import config from '../../app/config';
import * as RootNavigation from '../../app/navigation/RootNavigation';
import {useStores} from '../../app/stores/use-stores';
import {
  NAVIGATION_STATE_KEY,
  NAVIGATION_TIMESTAMP_KEY,
  NAVIGATION_URL_KEY,
} from '../constants/sessionStorageKeys';
import {AppRoute} from '../models/AppRoute';
import {navStructure} from '../navigation/linking';
import { clearLastNotificationUrl,getLastNotificationUrl } from '../utils/onesignal';

const theme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: 'white',
  },
};

// We need to persist state, otherwise the back buttons don't work in the case
// of a refresh. See https://reactnavigation.org/docs/state-persistence/ and
// https://liquormarketinggroup.atlassian.net/browse/LLP-392

// After 10 minutes, we want to ignore this and allow hard refreshes.
// A scenario we want to preserve is that a user can manually change the URL
// in the address bar, and they don't get redirected or have weird navigation
// stack state loaded in because of this solution
const STATE_EXPIRATION_TIME = 10 * 60 * 1000; // 10 minutes

const getCurrentRouteName = () =>
  RootNavigation?.navigationRef?.getCurrentRoute()?.name;

export default function AppNavigationContainer({
  children,
  initialState,
}: {
  children: React.ReactNode;
  initialState?: InitialState;
}) {
  const {globalStore} = useStores();
  const [isReady, setIsReady] = React.useState(false);
  const [initialNavigationState, setInitialNavigationState] = React.useState<
    InitialState | undefined
  >(initialState);

  useEffect(() => {
    const restoreState = async () => {
      try {
        // sessionStorage is per tab, rather than localStorage, which is per domain
        // this is safest behaviour for state restoration via opening new links
        const savedStateString = sessionStorage.getItem(NAVIGATION_STATE_KEY);
        const savedTimestamp = sessionStorage.getItem(NAVIGATION_TIMESTAMP_KEY);
        const savedUrl = sessionStorage.getItem(NAVIGATION_URL_KEY);

        const currentTimestamp = Date.now();
        const currentUrl = window.location.href;

        if (savedStateString && savedTimestamp && savedUrl) {
          const state = JSON.parse(savedStateString);
          const timestamp = parseInt(savedTimestamp, 10);

          const isExpired =
            currentTimestamp - timestamp > STATE_EXPIRATION_TIME;
          const isSameUrl = currentUrl === savedUrl;

          if (!isExpired && isSameUrl) {
            setInitialNavigationState(state);
          } else {
            sessionStorage.removeItem(NAVIGATION_STATE_KEY);
            sessionStorage.removeItem(NAVIGATION_TIMESTAMP_KEY);
            sessionStorage.removeItem(NAVIGATION_URL_KEY);
          }
        }
      } catch (error) {
        // If an error occurs during state restoration, clear the persisted state
        // So the user don't get stuck in a loop of errors and refresh doesn't recify the issue
        sessionStorage.removeItem(NAVIGATION_STATE_KEY);
        sessionStorage.removeItem(NAVIGATION_TIMESTAMP_KEY);
        sessionStorage.removeItem(NAVIGATION_URL_KEY);
      } finally {
        setIsReady(true);
      }
    };

    // if (!initialState && Platform.OS === 'web') {
    // TODO: at the moment this solution causes a severe impact bug, where the user
    // tries to change their store, and after the store is selected, instead of navigating
    // home, it will navigate back to the store selection page. I.e it gets stuck
    // on /MY_STORE_STORE_SELECTION
    // restoreState();
    // } else {
    setIsReady(true);
    // }
  }, [initialState]);

  const linking: LinkingOptions<{}> = {
    // Prefixes for Mobile
    prefixes: [
      `${config.WHITE_LABEL_ID}://`,
      `https://${config.HOST_DOMAIN}`,
      `https://beta.${config.HOST_DOMAIN}`,
      `https://${config.WHITE_LABEL_ID}-brand-dev.lmg-bw.au`, // test domain
    ],
    config: navStructure,
    getStateFromPath: (path: string, options?: any) => {
      const originalNavigationState = getStateFromPath(path, options);
      globalStore.setDelayedNavigationPath(path);
      return originalNavigationState;
    },
    getInitialURL: async () => {
      console.debug('[OneSignal] getInitialURL');

      const lastNotificationUrl = getLastNotificationUrl();
      if (lastNotificationUrl) {
        console.debug('[OneSignal] Returning last notification URL:', lastNotificationUrl);
        clearLastNotificationUrl();
        return lastNotificationUrl;
      }

      return await Linking.getInitialURL();
    }
  };

  const routeNameRef = useRef<any>();

  const handleNavigationContainerReady = () =>
    (routeNameRef.current = getCurrentRouteName());

  const handleNavigationContainerStateChange = () => {
    const currentRouteName = getCurrentRouteName() || '';
    const previousRouteName = routeNameRef.current;
    if (previousRouteName !== currentRouteName) {
      globalStore.setIsAccountRoute(false);
      globalStore.setIsInformationRoute(false);
      console.log(`navigating to route ${currentRouteName}`);
    }
  };

  const handleUnhandledAction = () => RootNavigation.navigate(AppRoute.HOME);

  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      onReady={handleNavigationContainerReady}
      onStateChange={state => {
        handleNavigationContainerStateChange();
        // TODO uncomment this when we fix restoreState() above
        // if (Platform.OS === 'web') {
        //   sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(state));
        //   sessionStorage.setItem(
        //     NAVIGATION_TIMESTAMP_KEY,
        //     Date.now().toString(),
        //   );
        //   // Slight hack, to ensure window.location.href has updated
        //   setTimeout(() => {
        //     sessionStorage.setItem(NAVIGATION_URL_KEY, window.location.href);
        //   }, 0);
        // }
      }}
      onUnhandledAction={handleUnhandledAction}
      initialState={initialNavigationState}
      linking={linking}
      ref={RootNavigation.navigationRef}
      theme={theme}
      documentTitle={{
        formatter: () => config.APP_NAME,
      }}>
      {children}
    </NavigationContainer>
  );
}
